import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, Box, Typography, IconButton, Chip } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ImageCarousel from './ImageCarousel';
import SaleBadge from './SaleBadge';
import { Sale, SaleType, SOURCE_WEBSITE_LABELS, SOURCE_WEBSITE_COLORS } from '../types';

interface SaleCardProps {
  sale: Sale;
  isFavorite?: boolean;
  onFavoriteToggle?: (saleId: string) => void;
  compact?: boolean;
  isUnavailable?: boolean;
}

const inferSaleType = (sale: Sale): SaleType => {
  const title = sale.title?.toLowerCase() || '';
  const source = sale.source_website?.toLowerCase() || '';
  const description = sale.description?.toLowerCase() || '';
  
  if (title.includes('estate') || source.includes('estate') || description.includes('estate sale')) {
    return 'estate_sale';
  }
  if (title.includes('multi-family') || title.includes('multi family') || source.includes('multi')) {
    return 'multi_family';
  }
  if (title.includes('moving') || description.includes('moving sale')) {
    return 'moving_sale';
  }
  if (title.includes('fundraiser') || description.includes('fundraiser')) {
    return 'fundraiser';
  }
  
  return 'yard_sale';
};

const SaleCard: React.FC<SaleCardProps> = ({
  sale,
  isFavorite = false,
  onFavoriteToggle,
  compact = false,
  isUnavailable = false,
}) => {
  const navigate = useNavigate();
  const saleType = inferSaleType(sale);
  
  const handleClick = () => {
    navigate(`/sale/${sale.id}`);
  };
  
  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFavoriteToggle?.(sale.id);
  };
  
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };
  
  const formatTime = (timeStr: string | null) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };
  
  const photos = sale.sale_photos || [];

  const cardImageHeight = 140;
  const cardMinHeight = 220; // Fixed minimum height for each list item

  return (
    <Card 
      onClick={isUnavailable ? undefined : handleClick}
      sx={{ 
        cursor: isUnavailable ? 'default' : 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s, opacity 0.2s',
        '&:hover': {
          transform: isUnavailable ? 'none' : 'translateY(-2px)',
          boxShadow: isUnavailable ? 1 : 3,
        },
        borderRadius: 2,
        overflow: 'hidden',
        width: '100%',
        minHeight: cardMinHeight,
        height: 'auto',
        opacity: isUnavailable ? 0.6 : 1,
      }}
    >
      {/* Image Section - Full Width on Top */}
      <Box sx={{ position: 'relative', width: '100%', height: cardImageHeight, flexShrink: 0 }}>
        <ImageCarousel 
          photos={photos} 
          height={cardImageHeight}
          showDots={false}
        />
        
        {/* Badge Overlay */}
        <Box 
          sx={{ 
            position: 'absolute', 
            top: 8, 
            left: 8,
            display: 'flex',
            gap: 0.5,
          }}
        >
          <SaleBadge saleType={saleType} size="small" />
          {/* Source Website Badge — dev/localhost only */}
          {(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && sale.source_website && SOURCE_WEBSITE_LABELS[sale.source_website] && (
            <Chip
              label={SOURCE_WEBSITE_LABELS[sale.source_website]}
              size="small"
              style={{
                backgroundColor: SOURCE_WEBSITE_COLORS[sale.source_website],
                color: '#ffffff',
              }}
              sx={{
                fontWeight: 700,
                fontSize: '0.65rem',
                height: 22,
                '& .MuiChip-label': { px: 1 },
              }}
            />
          )}
        </Box>
        
        {/* Unavailable Badge */}
        {isUnavailable && (
          <Chip
            label="No longer available"
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              bgcolor: 'warning.main',
              color: 'white',
              fontWeight: 600,
            }}
          />
        )}
        
        {/* Favorite Button */}
        <IconButton
          onClick={isUnavailable ? undefined : handleFavoriteClick}
          disabled={isUnavailable}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'rgba(255,255,255,0.9)',
            width: 32,
            height: 32,
            '&:hover': {
              bgcolor: 'rgba(255,255,255,1)',
            },
          }}
          size="small"
        >
          {isFavorite ? (
            <FavoriteIcon sx={{ fontSize: 18, color: 'error.main' }} />
          ) : (
            <FavoriteBorderIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>
        
        {/* Photo Count */}
        {photos.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.6)',
              color: 'white',
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontSize: '0.7rem',
              fontWeight: 500,
            }}
          >
            {photos.length} photos
          </Box>
        )}
      </Box>
      
      {/* Content Section - Below Image */}
      <CardContent sx={{ 
        p: 1.5, 
        '&:last-child': { pb: 1.5 },
        minHeight: cardMinHeight - cardImageHeight,
      }}>
        <Typography 
          variant="subtitle2" 
          sx={{ 
            fontWeight: 700,
            fontSize: '0.95rem',
            mb: 0.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            lineHeight: 1.3,
          }}
        >
          {sale.title}
        </Typography>
        
        <Typography 
          variant="caption" 
          color="text.secondary"
          sx={{
            display: 'block',
            mb: 0.5,
          }}
        >
          {sale.address}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CalendarTodayIcon sx={{ fontSize: 14, color: 'primary.main' }} />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              {formatDate(sale.start_date)}
            </Typography>
          </Box>
          
          {sale.start_time && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AccessTimeIcon sx={{ fontSize: 14, color: 'primary.main' }} />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                {formatTime(sale.start_time)}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default SaleCard;
